import { useState, type ReactNode } from 'react';
import { Download, Table2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { cn } from '@/shared/lib/cn';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import {
  CASAS_DOLAR,
  CASAS_DOLAR_LABEL,
  type CasaDolar,
} from '@/shared/lib/tipoCambio';
import {
  useStockDashboard,
  type StockDashboard,
  type StockDetalle,
  type StockMonto,
  type VistaStock,
} from './ventasApi';
import { fmtInt, fmtMoneda, fmtMonedaCompact } from './dashFormat';
import { CAT_PALETTE, buildCatColor, DonutChart, type DonutSlice } from './ventasCharts';
import { EmptyCard, KpiSkeletonRow, SegBar } from './dashUi';
import { descargarStockXls } from './stockXls';
import { ReposicionView } from './ReposicionView';

export function StockSub({ nav }: { nav?: ReactNode }) {
  const [modo, setModo] = useState<'valor' | 'reposicion'>('valor');
  const leftControls = (
    <>
      {nav}
      <SegBar
        value={modo}
        onChange={setModo}
        options={[
          { id: 'valor', label: 'Valorización' },
          { id: 'reposicion', label: 'Reposición' },
        ]}
      />
    </>
  );
  return modo === 'valor' ? (
    <ValorizacionView leftControls={leftControls} />
  ) : (
    <ReposicionView leftControls={leftControls} />
  );
}

function ValorizacionView({ leftControls }: { leftControls?: ReactNode }) {
  const [vista, setVista] = useState<VistaStock>('ars');
  const [casa, setCasa] = useState<CasaDolar>('oficial');
  const [detalleOpen, setDetalleOpen] = useState(false);
  const q = useStockDashboard(vista, casa);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {leftControls}
          <Button variant="outline" size="sm" onClick={() => setDetalleOpen(true)} disabled={!q.data}>
            <Table2 className="mr-2 h-4 w-4" aria-hidden />
            Ver detalle
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => q.data && void descargarStockXls(q.data)}
            disabled={!q.data}
          >
            <Download className="mr-2 h-4 w-4" aria-hidden />
            Descargar XLS
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <SegBar
            value={vista}
            onChange={setVista}
            options={[
              { id: 'ars', label: 'ARS' },
              { id: 'usd', label: 'USD' },
            ]}
          />
          {vista === 'usd' && (
            <SegBar
              value={casa}
              onChange={setCasa}
              options={CASAS_DOLAR.map((c) => ({ id: c, label: CASAS_DOLAR_LABEL[c] }))}
            />
          )}
        </div>
      </div>

      {q.isError ? (
        <EmptyCard>No se pudo cargar el stock. Reintentá en unos segundos.</EmptyCard>
      ) : q.isLoading || !q.data ? (
        <KpiSkeletonRow n={4} />
      ) : (
        <StockLoaded data={q.data} />
      )}

      {q.data && <DetalleDialog open={detalleOpen} onOpenChange={setDetalleOpen} data={q.data} />}
    </div>
  );
}

function StockLoaded({ data }: { data: StockDashboard }) {
  const moneda = data.moneda;
  const tipoColor = buildCatColor(data.porTipo.map((t) => t.tipo));
  const fmtVal = (n: number) => fmtMonedaCompact(n, moneda);

  const estadoSlices: DonutSlice[] = data.porEstado.map((e, i) => ({
    label: e.estado,
    value: e.valor,
    color: CAT_PALETTE[i % CAT_PALETTE.length],
    sub: `${fmtInt(e.unidades)} u`,
  }));
  const tipoSlices: DonutSlice[] = data.porTipo.map((t) => ({
    label: t.tipo,
    value: t.valor,
    color: tipoColor(t.tipo),
    sub: `${fmtInt(t.articulos)} art · ${fmtInt(t.unidades)} u`,
  }));

  const cotNota =
    data.vista === 'usd'
      ? data.cotizacion
        ? `Dólar ${CASAS_DOLAR_LABEL[data.cotizacion.casa as CasaDolar] ?? data.cotizacion.casa} · ${data.cotizacion.fecha} · $ ${fmtInt(data.cotizacion.valor)}`
        : 'Sin cotización del dólar — se muestra en ARS'
      : `A precio de venta · al ${data.alFecha}`;

  return (
    <>
      {/* Card combinada: hero + sub-métricas por estado */}
      <Card>
        <CardContent className="flex flex-col gap-4 p-5">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Stock total valorizado{data.vista === 'usd' ? ' · USD' : ''}
              </span>
              <span className="text-3xl font-semibold tracking-tight text-foreground">
                {fmtMoneda(data.stockTotal.valor, moneda)}
              </span>
              <span className="text-xs text-muted-foreground">
                {fmtInt(data.articulos)} artículos · {fmtInt(data.stockTotal.unidades)} u · {cotNota}
              </span>
            </div>
            {data.sinPrecio > 0 && (
              <span
                className="rounded-md px-2 py-1 text-xs font-medium"
                style={{ color: '#eda100', backgroundColor: 'rgba(237,161,0,0.12)' }}
              >
                {fmtInt(data.sinPrecio)} artículos sin precio (valorizan $0)
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-4 border-t pt-4 sm:grid-cols-3 lg:grid-cols-5">
            <StatMini label="Disponible" m={data.disponible} moneda={moneda} />
            <StatMini label="Sin vender" m={data.sinVender} moneda={moneda} />
            <StatMini label="Comprometido" m={data.comprometido} moneda={moneda} />
            <StatMini label="Por ingresar" m={data.porIngresar} moneda={moneda} />
            <StatMini label="Préstamo / SR" m={data.prestamo} moneda={moneda} />
          </div>
        </CardContent>
      </Card>

      {data.cotizacionNoDisponible && (
        <p className="text-xs" style={{ color: '#d03b3b' }}>
          No se pudo obtener la cotización del dólar — los valores se muestran en ARS.
        </p>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Composición por estado</CardTitle>
          </CardHeader>
          <CardContent>
            <DonutChart slices={estadoSlices} formatValue={fmtVal} centerLabel="Stock" emptyText="Sin stock cargado." />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Composición por tipo</CardTitle>
          </CardHeader>
          <CardContent>
            <DonutChart slices={tipoSlices} formatValue={fmtVal} centerLabel="Stock" emptyText="Sin stock cargado." />
          </CardContent>
        </Card>
      </div>
    </>
  );
}

function StatMini({ label, m, moneda }: { label: string; m: StockMonto; moneda: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <span className="text-base font-semibold tabular-nums text-foreground">
        {fmtMonedaCompact(m.valor, moneda)}
      </span>
      <span className="text-xs tabular-nums text-muted-foreground">{fmtInt(m.unidades)} u</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dialog "Ver detalle" — grilla por artículo (con headers agrupados + zebra)
// ---------------------------------------------------------------------------

type Col = {
  label: string;
  width: string;
  get: (r: StockDetalle) => string;
  num?: boolean;
  emphasis?: boolean;
  groupEnd?: boolean;
};

function DetalleDialog({
  open,
  onOpenChange,
  data,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  data: StockDashboard;
}) {
  const m = data.moneda;
  const cols: Col[] = [
    { label: 'Artículo', width: '19%', get: (r) => r.articulo.toUpperCase() },
    { label: 'Modelo', width: '8%', get: (r) => r.modelo || '—' },
    { label: 'Tipo', width: '8%', get: (r) => r.tipo, groupEnd: true },
    { label: 'Disp.', width: '5%', get: (r) => fmtInt(r.disponible), num: true },
    { label: 'P.pend.', width: '5%', get: (r) => fmtInt(r.reservado), num: true },
    { label: 'P.parc.', width: '5%', get: (r) => fmtInt(r.reservadoParcial), num: true },
    { label: 'P.desp.', width: '5%', get: (r) => fmtInt(r.pendienteDespacho), num: true },
    { label: 'Ingr.', width: '5%', get: (r) => fmtInt(r.porIngresar), num: true },
    { label: 'Prést.', width: '5%', get: (r) => fmtInt(r.prestShow), num: true },
    { label: 'Total', width: '6%', get: (r) => fmtInt(r.totalStock), num: true, emphasis: true, groupEnd: true },
    { label: 'Precio', width: '8%', get: (r) => (r.precio != null ? fmtMonedaCompact(r.precio, m) : '—'), num: true },
    { label: 'Sin vender', width: '9%', get: (r) => fmtMonedaCompact(r.sinVender, m), num: true },
    { label: 'Valor', width: '9%', get: (r) => fmtMonedaCompact(r.valor, m), num: true, emphasis: true },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[min(98vw,84rem)]">
        <DialogHeader className="flex flex-row items-center justify-between gap-3 pr-8">
          <DialogTitle>
            Inventario valorizado — detalle ({fmtInt(data.articulos)} artículos ·{' '}
            {data.vista === 'usd' ? 'USD' : 'ARS'})
          </DialogTitle>
          <Button variant="outline" size="sm" onClick={() => void descargarStockXls(data)}>
            <Download className="mr-2 h-4 w-4" aria-hidden />
            Descargar XLS
          </Button>
        </DialogHeader>
        <DialogBody className="max-h-[72vh] overflow-y-auto overflow-x-hidden px-0">
          <table className="w-full table-fixed border-collapse text-sm">
            <colgroup>
              {cols.map((c) => (
                <col key={c.label} style={{ width: c.width }} />
              ))}
            </colgroup>
            <thead className="sticky top-0 z-10">
              {/* Fila de grupos */}
              <tr className="bg-muted/60 text-[10px] uppercase tracking-wider text-muted-foreground">
                <th colSpan={3} className="border-b border-r px-2 py-1.5 text-left font-semibold">
                  Producto
                </th>
                <th colSpan={7} className="border-b border-r px-2 py-1.5 text-center font-semibold">
                  Cantidades por estado
                </th>
                <th colSpan={3} className="border-b px-2 py-1.5 text-center font-semibold">
                  Valorizado
                </th>
              </tr>
              {/* Fila de columnas */}
              <tr className="bg-card">
                {cols.map((c) => (
                  <th
                    key={c.label}
                    className={cn(
                      'border-b px-2 py-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground',
                      c.num ? 'text-right' : 'text-left',
                      c.groupEnd && 'border-r',
                    )}
                  >
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.detalle.map((r, i) => (
                <tr key={`${r.articulo}-${i}`} className={cn(i % 2 === 1 && 'bg-muted/30', 'hover:bg-muted/60')}>
                  {cols.map((c) => (
                    <td
                      key={c.label}
                      title={c.label === 'Artículo' ? r.articulo.toUpperCase() : undefined}
                      className={cn(
                        'overflow-hidden text-ellipsis whitespace-nowrap px-2 py-1.5',
                        c.num ? 'text-right tabular-nums' : 'text-left',
                        c.groupEnd && 'border-r',
                        c.label === 'Artículo'
                          ? 'font-medium text-foreground'
                          : c.emphasis
                            ? 'font-medium text-foreground'
                            : 'text-muted-foreground',
                      )}
                    >
                      {c.get(r)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}